import { ViberAgentBuilder } from '@bpinternal/viber-ui'
import { AlertTitle, ColoredAlert } from '@bpinternal/ui-kit-next'
import { createFileRoute } from '@tanstack/react-router'
import cookie from 'js-cookie'
import { useEffect, useRef, useState } from 'react'
import { Page } from '~/components'
import { BpSpinner } from '~/elements'
import { useFeature } from '~/hooks/useFeature'
import { config, PAT_KEY, viberApiBaseUrl } from '~/shared'
import { getApiClient, useLocalStore } from '~/stores'

export const Route = createFileRoute('/workspaces/$workspaceId/bots/$botId/vibe')({
  component: Component,
})

type ViberPatState =
  | { status: 'idle' | 'loading'; pat?: never; error?: never }
  | { status: 'ready'; pat: string; error?: never }
  | { status: 'error'; pat?: never; error: string }

const VIBE_PAGE_CLASSNAME = 'flex h-[calc(100dvh-6.5rem)] min-h-0 max-w-none flex-col md:h-[calc(100dvh-6rem)]'
const vibePageShellClassName = 'dashboard-viber-embed h-full min-h-0 w-full overflow-hidden bg-[#161618]'

function useDashboardViberPat({ enabled }: { enabled: boolean }): ViberPatState {
  const staticPat = config.usePat ? config.pat ?? cookie.get(PAT_KEY) : undefined
  const [state, setState] = useState<ViberPatState>({ status: 'idle' })
  const requestStartedRef = useRef(false)

  useEffect(() => {
    if (!enabled || staticPat || config.usePat || requestStartedRef.current) return
    requestStartedRef.current = true

    let cancelled = false
    getApiClient()
      .createPersonalAccessToken({ note: `Vibe Dashboard ${new Date().toISOString()}` })
      .then(({ pat }) => {
        if (!cancelled) setState({ status: 'ready', pat: pat.value })
      })
      .catch((error: { message?: string }) => {
        if (!cancelled) setState({ status: 'error', error: error?.message ?? 'Failed to create Vibe token.' })
      })

    return () => {
      cancelled = true
    }
  }, [enabled, staticPat])

  if (!enabled) return { status: 'idle' }
  if (staticPat) return { status: 'ready', pat: staticPat }
  if (config.usePat) {
    return { status: 'error', error: 'Vibe requires a PAT-backed dashboard session for this environment.' }
  }
  if (state.status === 'idle') return { status: 'loading' }
  return state
}

function Component() {
  const { botId, workspaceId } = Route.useParams()
  const { user } = Route.useRouteContext()
  const accountId = user.id
  const theme = useLocalStore((state) => state.theme)
  const isViberEnabled = useFeature('dashboard-viber')
  const viberPat = useDashboardViberPat({ enabled: isViberEnabled })

  if (!isViberEnabled) {
    return (
      <Page className={VIBE_PAGE_CLASSNAME}>
        <div className={`flex ${vibePageShellClassName} items-center justify-center`}>
          <ColoredAlert color="gray" className="max-w-xl">
            <AlertTitle>Vibe is not enabled for this dashboard.</AlertTitle>
          </ColoredAlert>
        </div>
      </Page>
    )
  }

  if (!accountId) {
    return (
      <Page className={VIBE_PAGE_CLASSNAME}>
        <div className={`flex ${vibePageShellClassName} items-center justify-center`}>
          <ColoredAlert color="gray" className="max-w-xl">
            <AlertTitle>Vibe requires a signed-in dashboard user.</AlertTitle>
          </ColoredAlert>
        </div>
      </Page>
    )
  }

  if (viberPat.status === 'error') {
    return (
      <Page className={VIBE_PAGE_CLASSNAME}>
        <div className={`flex ${vibePageShellClassName} items-center justify-center`}>
          <ColoredAlert color="gray" className="max-w-xl">
            <AlertTitle>{viberPat.error}</AlertTitle>
          </ColoredAlert>
        </div>
      </Page>
    )
  }

  if (viberPat.status !== 'ready') {
    return (
      <Page className={VIBE_PAGE_CLASSNAME}>
        <div className={`flex ${vibePageShellClassName} items-center justify-center`}>
          <BpSpinner className="h-20 w-20" />
        </div>
      </Page>
    )
  }

  return (
    <Page className={VIBE_PAGE_CLASSNAME}>
      <div className={vibePageShellClassName}>
        <ViberAgentBuilder
          botId={botId}
          workspaceId={workspaceId}
          credentials={{ pat: viberPat.pat, accountId }}
          apiBaseUrl={viberApiBaseUrl}
          theme={theme}
          embedded
        />
      </div>
    </Page>
  )
}
